import { Button, Card, Form, Input, Modal, Popconfirm, Space, Switch, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type FeeHead = {
  id: string;
  code: string;
  name: string;
  tax_intent?: string | null;
  is_active?: number | boolean;
};

type HeadForm = {
  code: string;
  name: string;
  tax_intent?: string;
  is_active?: boolean;
};

export default function FeeHeadsPage() {
  const { t } = useTranslation('education');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.fee.manage') || user?.permissions?.includes('education.admin')
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<FeeHead | null>(null);
  const [createForm] = Form.useForm<HeadForm>();
  const [editForm] = Form.useForm<HeadForm>();

  const query = useQuery({
    queryKey: ['education-fee-heads'],
    queryFn: () => api.get<FeeHead[]>('/education/fees/heads'),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['education-fee-heads'] });

  const create = useMutation({
    mutationFn: (values: HeadForm) => api.post('/education/fees/heads', values),
    onSuccess: () => {
      message.success(t('feeHeadCreated'));
      setCreateOpen(false);
      createForm.resetFields();
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const update = useMutation({
    mutationFn: (values: HeadForm & { id: string }) =>
      api.put(`/education/fees/heads/${values.id}`, {
        code: values.code,
        name: values.name,
        tax_intent: values.tax_intent,
        is_active: values.is_active,
      }),
    onSuccess: () => {
      message.success(t('feeHeadUpdated'));
      setEditing(null);
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/education/fees/heads/${id}`),
    onSuccess: () => {
      message.success(t('feeHeadDeleted'));
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const openEdit = (row: FeeHead) => {
    setEditing(row);
    editForm.setFieldsValue({
      code: row.code,
      name: row.name,
      tax_intent: row.tax_intent || 'exempt_intent_documentation',
      is_active: !(row.is_active === 0 || row.is_active === false),
    });
  };

  return (
    <EducationPageChrome
      title={t('feeHeads')}
      description={t('feeHeadsIntro')}
      extra={
        <Space>
          {canManage ? (
            <Button
              type="primary"
              onClick={() => {
                createForm.setFieldsValue({
                  is_active: true,
                  tax_intent: 'exempt_intent_documentation',
                });
                setCreateOpen(true);
              }}
            >
              {t('newFeeHead')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/fees')}>{t('backToFees')}</Button>
        </Space>
      }
    >
      <Card size="small">
        <Table
          rowKey="id"
          size="small"
          loading={query.isLoading}
          dataSource={query.data?.data ?? []}
          pagination={{ pageSize: 25 }}
          columns={[
            { title: t('code'), dataIndex: 'code', width: 120 },
            { title: t('name'), dataIndex: 'name' },
            { title: t('taxIntent'), dataIndex: 'tax_intent', ellipsis: true },
            {
              title: t('status'),
              dataIndex: 'is_active',
              width: 100,
              render: (v) =>
                v === 0 || v === false ? <Tag>{t('inactive')}</Tag> : <Tag color="success">{t('active')}</Tag>,
            },
            ...(canManage
              ? [
                  {
                    title: t('actions'),
                    width: 180,
                    render: (_: unknown, row: FeeHead) => (
                      <Space>
                        <Button size="small" onClick={() => openEdit(row)}>
                          {tc('edit')}
                        </Button>
                        <Popconfirm
                          title={t('feeHeadDeleteConfirm')}
                          onConfirm={() => remove.mutate(row.id)}
                        >
                          <Button size="small" danger loading={remove.isPending}>
                            {tc('delete')}
                          </Button>
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>

      <Modal
        title={t('newFeeHead')}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{ is_active: true, tax_intent: 'exempt_intent_documentation' }}
          onFinish={(values) => create.mutate(values)}
        >
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="tax_intent" label={t('taxIntent')}>
            <Input />
          </Form.Item>
          <Form.Item name="is_active" label={t('active')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={t('editFeeHead')}
        open={Boolean(editing)}
        onCancel={() => setEditing(null)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) => editing && update.mutate({ ...values, id: editing.id })}
        >
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="tax_intent" label={t('taxIntent')}>
            <Input />
          </Form.Item>
          <Form.Item name="is_active" label={t('active')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={update.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>
    </EducationPageChrome>
  );
}
